import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Square, X, Trash2, Send } from 'lucide-react';
import {
  buildRecordingFile,
  createPreviewPlaybackUrl,
  getRecordingMime,
} from '../utils/audioRecording';

interface VoiceRecorderProps {
  onRecordingComplete: (file: File) => void;
  onCancel?: () => void;
  maxDurationMs?: number;
  fullWidth?: boolean;
  autoStart?: boolean;
  confirmBeforeSend?: boolean;
}

export default function VoiceRecorder({
  onRecordingComplete,
  onCancel,
  maxDurationMs = 120000,
  fullWidth = false,
  autoStart = false,
  confirmBeforeSend = false,
}: VoiceRecorderProps) {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<{
    file: File;
    playbackUrl: string;
    durationMs: number;
  } | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const autoStartedRef = useRef(false);
  const mimeRef = useRef(getRecordingMime());
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const formatDuration = (ms: number): string => {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const revokeUrl = useCallback((url?: string) => {
    if (url) URL.revokeObjectURL(url);
  }, []);

  const clearPreview = useCallback(() => {
    setPreview((prev) => {
      if (prev?.playbackUrl) revokeUrl(prev.playbackUrl);
      return null;
    });
    setPreviewLoading(false);
  }, [revokeUrl]);

  const processStoppedRecording = useCallback(async () => {
    const { blobMime, extension } = mimeRef.current;
    const elapsed = Date.now() - startTimeRef.current;
    const file = buildRecordingFile(chunksRef.current, blobMime, extension);
    chunksRef.current = [];

    if (!file) {
      setError(t('voice.recordingError'));
      setPreviewLoading(false);
      return;
    }

    if (confirmBeforeSend) {
      setPreviewLoading(true);
      try {
        const playbackUrl = await createPreviewPlaybackUrl(file);
        setPreview({ file, playbackUrl, durationMs: elapsed });
      } catch {
        setError(t('voice.recordingError'));
      } finally {
        setPreviewLoading(false);
      }
    } else {
      onRecordingComplete(file);
    }
  }, [confirmBeforeSend, onRecordingComplete, t]);

  const stopRecordingInternal = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') return;
    rec.stop();
    mediaRecorderRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      clearPreview();
      chunksRef.current = [];
      mimeRef.current = getRecordingMime();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { recordMime } = mimeRef.current;

      const recorder = new MediaRecorder(stream, { mimeType: recordMime });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        // Request any remaining data that hasn't been delivered yet
        try { recorder.requestData(); } catch { /* ignore */ }
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (timerRef.current) clearInterval(timerRef.current);
        setIsRecording(false);
        setDuration(0);
        // Small delay to let requestData complete delivering chunks
        window.setTimeout(() => {
          void processStoppedRecording();
        }, 150);
      };

      mediaRecorderRef.current = recorder;
      // timeslice=1000 ensures ondataavailable fires every second even
      // on browsers that don't emit data when stop() is called.
      recorder.start(1000);
      startTimeRef.current = Date.now();
      setIsRecording(true);

      timerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setDuration(elapsed);
        if (elapsed >= maxDurationMs) {
          stopRecordingInternal();
        }
      }, 200);
    } catch (err: unknown) {
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError') {
        setError(t('voice.permissionDenied'));
      } else {
        setError(t('voice.recordingError'));
      }
    }
  }, [maxDurationMs, t, clearPreview, processStoppedRecording, stopRecordingInternal]);

  const stopRecording = useCallback(() => {
    stopRecordingInternal();
  }, [stopRecordingInternal]);

  const cancelRecording = useCallback(() => {
    chunksRef.current = [];
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = null;
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
      mediaRecorderRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
    setDuration(0);
    setPreviewLoading(false);
    clearPreview();
    onCancel?.();
  }, [onCancel, clearPreview]);

  const discardPreview = useCallback(() => {
    clearPreview();
    onCancel?.();
  }, [clearPreview, onCancel]);

  const confirmPreview = useCallback(() => {
    if (!preview) return;
    onRecordingComplete(preview.file);
    revokeUrl(preview.playbackUrl);
    setPreview(null);
  }, [preview, onRecordingComplete, revokeUrl]);

  useEffect(() => {
    if (!preview?.playbackUrl || !audioRef.current) return;
    const el = audioRef.current;
    el.src = preview.playbackUrl;
    el.load();
  }, [preview?.playbackUrl]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        rec.onstop = null;
        rec.stop();
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
      if (preview?.playbackUrl) revokeUrl(preview.playbackUrl);
    };
  }, [preview?.playbackUrl, revokeUrl]);

  useEffect(() => {
    if (autoStart && !autoStartedRef.current && !isRecording && !error && !preview && !previewLoading) {
      autoStartedRef.current = true;
      void startRecording();
    }
  }, [autoStart, isRecording, error, preview, previewLoading, startRecording]);

  const barClass = fullWidth ? 'w-full' : '';

  if (error) {
    return (
      <div className={`flex items-center gap-2 text-danger-500 text-sm ${barClass}`}>
        <X className="w-4 h-4 shrink-0" />
        <span className="flex-1">{error}</span>
        <button type="button" onClick={() => setError(null)} className="underline text-xs shrink-0">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (previewLoading) {
    return (
      <div className={`flex items-center justify-center gap-2 py-4 text-sm text-surface-600 ${barClass}`}>
        <span className="inline-block w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        {t('voice.preparingPreview')}
      </div>
    );
  }

  if (preview) {
    return (
      <div
        className={`flex items-center gap-3 bg-surface-50 rounded-xl px-4 py-3 border border-surface-200 shadow-xs ${barClass}`}
      >
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <span className="text-xs text-surface-600">{t('voice.previewReady')}</span>
          <span className="text-sm font-mono text-surface-800 tabular-nums">
            {formatDuration(preview.durationMs)}
          </span>
          {/* Preview of the user's own recording before it is sent: no caption file exists. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            ref={audioRef}
            key={preview.playbackUrl}
            controls
            className="w-full min-h-touch"
            preload="auto"
            playsInline
          />
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            discardPreview();
          }}
          className="p-2.5 text-danger-600 hover:bg-danger-50 rounded-full shrink-0"
          title={t('voice.deleteRecording')}
          aria-label={t('voice.deleteRecording')}
        >
          <Trash2 className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            confirmPreview();
          }}
          className="p-2.5 bg-accent-hover text-white rounded-full hover:bg-accent-pressed shrink-0 shadow-xs"
          title={t('voice.attachRecording')}
          aria-label={t('voice.attachRecording')}
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div
        className={`flex items-center gap-3 bg-danger-50 rounded-xl px-4 py-3 border border-danger-200 shadow-xs ${barClass}`}
      >
        <div className="w-3 h-3 bg-danger-500 rounded-full animate-pulse shrink-0" />
        <span className="text-sm font-mono text-danger-700 tabular-nums">{formatDuration(duration)}</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            stopRecording();
          }}
          className="p-1.5 bg-solid-danger text-white rounded-full hover:bg-solid-danger-hover"
          title={t('voice.stop')}
          aria-label={t('voice.stop')}
        >
          <Square className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            cancelRecording();
          }}
          className="p-1.5 text-surface-500 hover:text-surface-700"
          title={t('common.cancel')}
          aria-label={t('common.cancel')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void startRecording();
      }}
      className={
        fullWidth
          ? 'flex items-center justify-center gap-2 w-full px-4 py-3 text-sm rounded-xl border-2 border-dashed border-primary-300 bg-primary-50 hover:bg-accent-soft text-primary-800 min-h-14'
          : 'flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-surface-50 text-surface-600'
      }
      title={t('voice.record')}
    >
      <Mic className="w-5 h-5" />
      <span>{t('voice.record')}</span>
    </button>
  );
}
